/**
 * Monthly Checklist Repository Implementation
 * Implements the MonthlyChecklistRepository interface
 */

import { MonthlyChecklistRepository } from "../../domain/repositories/MonthlyChecklistRepository";
import {
  MonthlyChecklistEntity,
  ChecklistItemEntity,
} from "../../domain/entities/MonthlyChecklistEntity";
import {
  getChecklistById,
  getChecklistItems,
  getChecklistItemsByArea,
  addChecklistItem,
  updateChecklistItem,
  deleteChecklistItem,
} from "../api/monthlyChecklistApi";
import { MonthlyChecklistMapper } from "../mappers/MonthlyChecklistMapper";
import {
  MonthlyChecklistDTO,
  ChecklistItemsGroupedResponseDTO,
  ChecklistItemsPaginatedResponseDTO,
  AddChecklistItemRequestDTO,
  AddChecklistItemResponseDTO,
  UpdateChecklistItemRequestDTO,
} from "../models/MonthlyChecklistDTOs";

export class MonthlyChecklistRepositoryImpl
  implements MonthlyChecklistRepository
{
  async getChecklistById(
    checklistId: number
  ): Promise<MonthlyChecklistEntity | null> {
    const response = (await getChecklistById(checklistId)) as {
      success: boolean;
      data?: { checklist?: MonthlyChecklistDTO; progress?: { total?: number; completed?: number; done?: number } } | null;
    };
    const data = response.data;
    if (!data?.checklist) {
      return null;
    }
    const entity = MonthlyChecklistMapper.toChecklistEntity(data.checklist);
    if (data.progress) {
      const p = data.progress;
      entity.progress = {
        total: p.total ?? 0,
        completed: p.completed ?? p.done ?? 0,
        in_progress: 0,
        not_started: 0,
        pending_review: 0,
      };
    }
    return entity;
  }

  async getChecklistItems(
    checklistId: number
  ): Promise<Record<string, ChecklistItemEntity[]>> {
    const response = (await getChecklistItems(
      checklistId
    )) as ChecklistItemsGroupedResponseDTO;
    if (!response.data) {
      return {};
    }

    // Convert grouped DTOs to grouped entities
    const grouped: Record<string, ChecklistItemEntity[]> = {};
    Object.entries(response.data).forEach(([area, items]) => {
      grouped[area] = MonthlyChecklistMapper.toItemEntityArray(items);
    });

    return grouped;
  }

  async getChecklistItemsByArea(
    checklistId: number,
    accountingArea: string,
    offset: number,
    limit: number
  ): Promise<{
    items: ChecklistItemEntity[];
    offset: number;
    limit: number;
    total: number;
    hasMore: boolean;
  }> {
    const response = (await getChecklistItemsByArea(
      checklistId,
      accountingArea,
      offset,
      limit
    )) as ChecklistItemsPaginatedResponseDTO;
    
    if (!response.data) {
      return {
        items: [],
        offset,
        limit,
        total: 0,
        hasMore: false,
      };
    }

    return {
      items: MonthlyChecklistMapper.toItemEntityArray(response.data.items),
      offset: response.data.offset,
      limit: response.data.limit,
      total: response.data.total,
      hasMore: response.data.has_more,
    };
  }

  async addChecklistItem(
    checklistId: number,
    item: Omit<ChecklistItemEntity, "id" | "checklist_id" | "created_at" | "updated_at">
  ): Promise<ChecklistItemEntity> {
    const requestData: AddChecklistItemRequestDTO = {
      checklist_id: checklistId,
      accounting_area: item.accounting_area,
      title: item.title,
      description: item.description,
      task_guidance: item.task_guidance,
      accepted_proof_types: item.accepted_proof_types,
      requires_proof: item.requires_proof,
      order_index: item.order_index,
      priority: item.priority,
      link_url: item.link_url,
      link_text: item.link_text,
      system_info_query: item.system_info_query,
      status: item.status,
    };

    const response = (await addChecklistItem(
      requestData
    )) as AddChecklistItemResponseDTO;

    if (!response.data || !response.data.item_id) {
      throw new Error("Failed to add item: No item ID returned");
    }

    // Return the item entity constructed from the response
    // The hook will add it locally and refresh will load full data
    return {
      id: response.data.item_id,
      checklist_id: checklistId,
      template_item_id: undefined,
      accounting_area: item.accounting_area,
      title: item.title,
      description: item.description,
      task_guidance: item.task_guidance,
      accepted_proof_types: item.accepted_proof_types,
      requires_proof: item.requires_proof,
      order_index: item.order_index,
      priority: item.priority,
      link_url: item.link_url,
      link_text: item.link_text,
      system_info_query: item.system_info_query,
      status: item.status || "not_started",
      notes: undefined,
      completed_by: undefined,
      completed_at: undefined,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
  }

  async updateChecklistItem(
    itemId: number,
    updates: Partial<
      Omit<
        ChecklistItemEntity,
        "id" | "checklist_id" | "created_at" | "updated_at" | "template_item_id"
      >
    >
  ): Promise<void> {
    const payload: UpdateChecklistItemRequestDTO = { item_id: itemId };

    if (updates.accounting_area !== undefined) {
      payload.accounting_area = updates.accounting_area;
    }
    if (updates.title !== undefined) {
      payload.title = updates.title;
    }
    if (updates.description !== undefined) {
      payload.description = updates.description;
    }
    if (updates.task_guidance !== undefined) {
      payload.task_guidance = updates.task_guidance;
    }
    if (updates.accepted_proof_types !== undefined) {
      payload.accepted_proof_types = updates.accepted_proof_types;
    }
    if (updates.requires_proof !== undefined) {
      payload.requires_proof = updates.requires_proof;
    }
    if (updates.order_index !== undefined) {
      payload.order_index = updates.order_index;
    }
    if (updates.priority !== undefined) {
      payload.priority = updates.priority;
    }
    if (updates.link_url !== undefined) {
      payload.link_url = updates.link_url;
    }
    if (updates.link_text !== undefined) {
      payload.link_text = updates.link_text;
    }
    if (updates.system_info_query !== undefined) {
      payload.system_info_query = updates.system_info_query;
    }
    if (updates.status !== undefined) {
      payload.status = updates.status;
    }
    if (updates.notes !== undefined) {
      payload.notes = updates.notes;
    }

    await updateChecklistItem(payload);
  }

  async deleteChecklistItem(itemId: number): Promise<void> {
    await deleteChecklistItem(itemId);
  }
}

